/**
 * Short screen-recorded tutorials served from /public/tutorials.
 * "tools" clips explain board editing; "practice" clips explain the AI tutor;
 * "classroom" covers live lessons with students.
 *
 * Every local clip ends with a "Try it free" end card meant for YouTube and
 * ads. `endAt` is the last second of board content; the in-app player stops
 * there so signed-in users never see the marketing card.
 */
export type TutorialSection = "tools" | "practice" | "classroom";

export type TutorialVideo = {
  id: string;
  /** Local mp4 path (played inline) or an external page URL (opened in a new tab). */
  src: string;
  /** External links render as a button instead of an inline player. */
  external?: boolean;
  /** Seconds. Playback pauses here to skip the end card. */
  endAt?: number;
  /**
   * Same cutoff for the Hebrew recording. Those files are longer, so the
   * English `endAt` would stop them mid-narration.
   */
  endAtHe?: number;
  section: TutorialSection;
  title: string;
  titleHe: string;
};

export const YOUTUBE_TUTORIAL_URL =
  "https://www.youtube.com/watch?v=0wVzIkOqlyM";

export const TUTORIAL_VIDEOS: TutorialVideo[] = [
  {
    id: "typing-math-symbols",
    src: "/tutorials/mathboard-typing-math-symbols.mp4",
    endAt: 39.1,
    endAtHe: 43.8,
    section: "tools",
    title: "Typing math symbols",
    titleHe: "הקלדת סימנים מתמטיים",
  },
  {
    id: "symbol-panel",
    src: "/tutorials/mathboard-symbol-panel.mp4",
    endAt: 61.3,
    endAtHe: 62.7,
    section: "tools",
    title: "Using the symbol panel",
    titleHe: "שימוש בלוח הסימנים",
  },
  {
    id: "toolbar-shapes-graphs-text",
    src: "/tutorials/mathboard-toolbar-shapes-graphs-text.mp4",
    endAt: 64,
    endAtHe: 67.4,
    section: "tools",
    title: "Toolbar: shapes, graphs, and text",
    titleHe: "סרגל הכלים: צורות, גרפים וטקסט",
  },
  {
    id: "students-ai-tutor",
    src: "/tutorials/mathboard-students-ai-tutor.mp4",
    endAt: 38.4,
    endAtHe: 42.6,
    section: "practice",
    title: "Practice with the AI tutor",
    titleHe: "תרגול עם מורה הבינה",
  },
  {
    id: "youtube-walkthrough",
    src: YOUTUBE_TUTORIAL_URL,
    external: true,
    section: "classroom",
    title: "Classroom walkthrough on YouTube",
    titleHe: "הדרכת כיתה ביוטיוב",
  },
];

export function tutorialsFor(section: TutorialSection): TutorialVideo[] {
  return TUTORIAL_VIDEOS.filter((v) => v.section === section);
}

export function tutorialById(id: string): TutorialVideo | undefined {
  return TUTORIAL_VIDEOS.find((v) => v.id === id);
}

/**
 * Local clips have a sibling `-he.mp4`. External links (the YouTube
 * walkthrough) stay on the same URL in both locales.
 */
export function tutorialSrc(video: TutorialVideo, hebrew = false): string {
  if (!hebrew || video.external) return video.src;
  return video.src.replace(/\.mp4$/, "-he.mp4");
}

/** Last second of board content for the file `tutorialSrc` would play. */
export function tutorialEndAt(
  video: TutorialVideo,
  hebrew = false,
): number | undefined {
  if (hebrew && video.endAtHe != null) return video.endAtHe;
  return video.endAt;
}

/** Trimmed length in whole seconds, rounded to the nearest 5 for labels. */
export function tutorialSeconds(
  video: TutorialVideo,
  hebrew = false,
): number | null {
  const endAt = tutorialEndAt(video, hebrew);
  if (endAt == null) return null;
  return Math.round(endAt / 5) * 5;
}
